// Compatibility layer: the original app was generated for Base44.
// This file replaces Base44 entirely (NO network calls to base44.com).
// - Authentication is handled by Supabase.
// - Data layer currently uses per-user localStorage collections (so the app stays functional).
// You can later swap the entity adapters to Supabase tables once your DB schema is ready.

import { supabase } from './supabaseClient';
import { 
  normalizeTerritory,
  getDefaultVatRatePercent,
  getDomUrssafBaseRatePercent,
  isVatApplicableInTerritory,
} from '@/utils/taxTerritories';
import { getDefaultUrssafRatePercent, getDefaultVersementLiberatoireRatePercent } from '@/utils/fiscalRates';

function nowIso() {
  return new Date().toISOString();
}

function uuidv4() {
  // Lightweight UUID (good enough for client-side ids)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function storageKey(entityName, userId) {
  return `tresopilot:${userId}:${entityName}`;
}

function readCollection(entityName, userId) {
  const raw = localStorage.getItem(storageKey(entityName, userId));
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeCollection(entityName, userId, items) {
  localStorage.setItem(storageKey(entityName, userId), JSON.stringify(items));
}

function applyFilter(items, filterObj = {}) {
  const entries = Object.entries(filterObj || {});
  if (entries.length === 0) return items;
  return items.filter((row) => {
    return entries.every(([k, v]) => {
      if (v === undefined || v === null) return true;
      // Basic equality filter
      return row?.[k] === v;
    });
  });
}

function applySort(items, sort) {
  if (!sort) return items;
  const desc = typeof sort === 'string' && sort.startsWith('-');
  const field = typeof sort === 'string' ? sort.replace(/^-/, '') : null;
  if (!field) return items;
  return [...items].sort((a, b) => {
    const av = a?.[field];
    const bv = b?.[field];
    if (av === bv) return 0;
    if (av === undefined || av === null) return 1;
    if (bv === undefined || bv === null) return -1;
    if (av > bv) return desc ? -1 : 1;
    return desc ? 1 : -1;
  });
}

function createLocalEntity(entityName) {
  return {
    async filter(filterObj = {}, sort, limit) {
      const user = await base44.auth.me();
      const items = readCollection(entityName, user.id);
      let out = applyFilter(items, filterObj);
      out = applySort(out, sort);
      if (typeof limit === 'number') out = out.slice(0, limit);
      return out;
    },

async list(sort = '-created_date', limit = 50) {
      return await this.filter({}, sort, limit);
    },

    async get(id) {
      const user = await base44.auth.me();
      const items = readCollection(entityName, user.id);
      return items.find((x) => x.id === id) || null;
    },

        async create(data) {
      const user = await base44.auth.me();
      const items = readCollection(entityName, user.id);
      const item = {
        id: uuidv4(),
        user_id: user.id,
        created_date: nowIso(),
        updated_date: nowIso(),
        ...data,
      };
      items.push(item);
      writeCollection(entityName, user.id, items);
      return item;
    },

    async update(id, data) {
      const user = await base44.auth.me();
      const items = readCollection(entityName, user.id);
      const idx = items.findIndex((x) => x.id === id);
      if (idx === -1) throw new Error(`${entityName}: item not found`);
      items[idx] = { ...items[idx], ...data, updated_date: nowIso() };
      writeCollection(entityName, user.id, items);
      return items[idx];
    },

    async delete(id) {
      const user = await base44.auth.me();
      const items = readCollection(entityName, user.id);
      const next = items.filter((x) => x.id !== id);
      writeCollection(entityName, user.id, next);
      return { success: true };
    },
  };
}

const base44Functions = {
  async invoke(name, payload) {
    console.log(`[functions.invoke] ${name}`, payload);

    // Real server-side functions are implemented as Supabase Edge Functions.
    // This keeps secrets (Stripe) out of the browser.
    const edgeFnNames = new Set([
      // Billing (Stripe)
      'createCheckoutSession',
      'createBillingPortal',
      'confirmPayment',
      // Entitlements refresh
      'syncStripeEntitlements',
      // TVA
      'generateVatPeriods',
      // Assistant IA
      'financialAssistant',
      // Rapports IA
      'generate_ai_report',
      // Rappels d’échéances par email + désinscription
      'sendDueReminders',
      // Rappels In-App pré-générés (cron)
      'syncInAppReminders',
      'createUnsubscribeToken',
      'unsubscribeByToken',
    ]);

    if (edgeFnNames.has(name)) {
      const { data, error } = await supabase.functions.invoke(name, {
        body: payload ?? {},
      });
      if (error) {
        // Try to surface the JSON error body from the Edge Function (Supabase wraps non-2xx).
        let detailed = error.message;
        try {
          const resp = error.context;
          if (resp && typeof resp.text === 'function') {
            const txt = await resp.text();
            if (txt) {
              try {
                const j = JSON.parse(txt);
                detailed = j.message || j.error || j.details || detailed;
              } catch {
                detailed = txt;
              }
            }
          }
        } catch {
          // ignore
        }
        // For background refresh calls we don't want a hard crash.
        if (name === 'syncStripeEntitlements') {
          console.warn(`[functions.invoke] ${name} failed:`, detailed);
          return { data: { ok: false, message: detailed } };
        }

        const e = new Error(detailed);
        e.name = error.name || 'EdgeFunctionError';
        throw e;
      }
      return { data };
    }

    // Provide safe defaults for exports to avoid runtime crashes.
    if (name?.toLowerCase().includes('export')) {
      return { data: { success: false, message: 'Export serveur non configuré.' } };
    }

    return { data: { success: false, message: 'Fonction non disponible.' } };
  },
};

// Proxy so existing code can call base44.functions.someName(payload)
// which internally maps to invoke('someName', payload).
const functionsProxy = new Proxy(base44Functions, {
  get(target, prop) {
    if (typeof prop === 'string' && prop in target) return target[prop];
    if (typeof prop === 'string') {
      return (payload) => target.invoke(prop, payload);
    }
    return undefined;
  },
});


// Supabase adapter for "payload jsonb" tables (revenues, charges, etc.)

// Adapter for Supabase "payload jsonb" tables (revenues, charges, etc.)
function flattenSupabaseRow(row) {
  const base = {
    id: row?.id,
    user_id: row?.user_id,
    created_date: row?.created_at,
    updated_date: row?.updated_at,
  };

  // Preferred model: { payload: jsonb }
  if (row && row.payload && typeof row.payload === 'object' && !Array.isArray(row.payload)) {
    return {
      ...base,
      ...(row.payload || {}),
    };
  }

  // Compatibility model: columns directly on the row (no payload column)
  const { id, user_id, created_at, updated_at, payload, ...rest } = row || {};
  return {
    ...base,
    ...(rest || {}),
  };
}

function isMissingColumnError(err, columnName) {
  const msg = String(err?.message || err?.details || err || '').toLowerCase();
  const col = String(columnName || '').toLowerCase();
  const code = String(err?.code || '').toLowerCase();

  // Postgres / SQL errors
  if (msg.includes('does not exist') && msg.includes(col)) return true;

  // PostgREST schema-cache errors (common on freshly created tables/columns)
  // Example: "Could not find the 'payload' column of 'cash_logs' in the schema cache"
  if ((code === 'pgrst204' || msg.includes('schema cache')) && msg.includes('could not find') && msg.includes(col)) {
    return true;
  }

  return false;
}

function createSupabaseEntity(tableName) {
  // Some tables in this project are stored as plain columns (no `payload` jsonb).
  // Trying the payload-model first triggers a PostgREST 400, then we fallback.
  // That fallback works but still produces noisy console/network errors.
  // We special-case those tables to use the column-model directly.
  const NON_PAYLOAD_TABLES = new Set(['cash_logs']);

  const api = {
    async filter(filterObj = {}, sort, limit) {
      const user = await base44.auth.me();

      // Guard: only allow reading own data
      if (filterObj?.user_id && filterObj.user_id !== user.id) return [];

      // NOTE:
      // Some installations use a "payload jsonb" column, others store fields directly as columns.
      // We use select('*') to avoid hard-failing when payload/updated_at do not exist.
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;

      let out = (data || []).map(flattenSupabaseRow);
      out = applyFilter(out, filterObj);
      out = applySort(out, sort);
      if (typeof limit === 'number') out = out.slice(0, limit);
      return out;
    },

    async list(sort = '-created_date', limit = 50) {
      return api.filter({}, sort, limit);
    },

    async get(id) {
      const user = await base44.auth.me();
      const { data, error } = await supabase
        .from(tableName)
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data ? flattenSupabaseRow(data) : null;
    },

    async create(data) {
      const user = await base44.auth.me();

      // Preserve explicit user_id if provided but enforce ownership
      const payload = { ...(data || {}) };
      delete payload.id;
      delete payload.user_id;
      delete payload.created_date;
      delete payload.updated_date;

      // If this table is known to have no payload column, write directly.
      if (NON_PAYLOAD_TABLES.has(tableName)) {
        const { data: row, error } = await supabase
          .from(tableName)
          .insert({ user_id: user.id, ...(payload || {}) })
          .select('*')
          .single();
        if (error) throw error;
        return flattenSupabaseRow(row);
      }

      // Try payload-model first, then fallback to direct columns.
      let insertErr;
      {
        const { data: row, error } = await supabase
          .from(tableName)
          .insert({ user_id: user.id, payload })
          .select('*')
          .single();
        if (!error) return flattenSupabaseRow(row);
        insertErr = error;
      }

      // Fallback: no payload column
      if (isMissingColumnError(insertErr, 'payload')) {
        const { data: row, error } = await supabase
          .from(tableName)
          .insert({ user_id: user.id, ...(payload || {}) })
          .select('*')
          .single();
        if (error) throw error;
        return flattenSupabaseRow(row);
      }

      throw insertErr;
    },

    // Insert many rows at once (used by "Générer les échéances" for example)
    async bulkCreate(items = []) {
      const user = await base44.auth.me();

      const rows = (items || []).map((it) => {
        const payload = { ...(it || {}) };
        delete payload.id;
        delete payload.user_id;
        delete payload.created_date;
        delete payload.updated_date;
        return { user_id: user.id, payload };
      });

      if (rows.length === 0) return [];

      // Bulk insert: try payload-model first, then fallback.
      {
        const { data, error } = await supabase
          .from(tableName)
          .insert(rows)
          .select('*');
        if (!error) return (data || []).map(flattenSupabaseRow);

        if (isMissingColumnError(error, 'payload')) {
          const flatRows = (items || []).map((it) => {
            const p = { ...(it || {}) };
            delete p.id;
            delete p.user_id;
            delete p.created_date;
            delete p.updated_date;
            return { user_id: user.id, ...p };
          });
          const { data: d2, error: e2 } = await supabase
            .from(tableName)
            .insert(flatRows)
            .select('*');
          if (e2) throw e2;
          return (d2 || []).map(flattenSupabaseRow);
        }

        throw error;
      }
    },

    async update(id, patch) {
      const user = await base44.auth.me();

      // Preferred: read + merge payload
      let readErr;
      let existing;
      {
        const res = await supabase
          .from(tableName)
          .select('id,payload')
          .eq('id', id)
          .eq('user_id', user.id)
          .single();
        existing = res.data;
        readErr = res.error;
      }

      // Fallback: direct columns (no payload)
      if (readErr && isMissingColumnError(readErr, 'payload')) {
        const cleanPatch = { ...(patch || {}) };
        delete cleanPatch.id;
        delete cleanPatch.user_id;
        delete cleanPatch.created_date;
        delete cleanPatch.updated_date;

        const { data: row, error } = await supabase
          .from(tableName)
          .update(cleanPatch)
          .eq('id', id)
          .eq('user_id', user.id)
          .select('*')
          .single();

        if (error) throw error;
        return flattenSupabaseRow(row);
      }

      if (readErr) throw readErr;

      const cleanPatch = { ...(patch || {}) };
      delete cleanPatch.id;
      delete cleanPatch.user_id;
      delete cleanPatch.created_date;
      delete cleanPatch.updated_date;

      const nextPayload = { ...(existing.payload || {}), ...cleanPatch };

      const { data: row, error } = await supabase
        .from(tableName)
        .update({ payload: nextPayload })
        .eq('id', id)
        .eq('user_id', user.id)
        .select('*')
        .single();

      if (error) throw error;
      return flattenSupabaseRow(row);
    },

    async delete(id) {
      const user = await base44.auth.me();
      const { error } = await supabase
        .from(tableName)
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;
      return { success: true };
    },
  };

  return api;
}


function createSupabaseEntitySafe(tableName, fallbackLocalName = tableName) {
  const sup = createSupabaseEntity(tableName);
  const local = createLocalEntity(fallbackLocalName);

  const isMissingTable = (e) => {
    const msg = (e?.message || '').toLowerCase();
    return msg.includes('does not exist') && msg.includes(tableName.toLowerCase());
  };

  const wrap = {};
  for (const k of ['filter','list','get','create','update','delete']) {
    if (typeof sup[k] !== 'function') continue;
    wrap[k] = async (...args) => {
      try {
        return await sup[k](...args);
      } catch (e) {
        if (isMissingTable(e)) {
          console.warn(`[Supabase] Table "${tableName}" manquante. Fallback localStorage pour "${fallbackLocalName}".`, e);
          if (typeof local[k] === 'function') return await local[k](...args);
        }
        throw e;
      }
    };
  }
  return wrap;
}

// --- Special tables (not payload-based) ---

// ACRE: we store the base URSSAF rate + ACRE metadata in profiles.rates_json.
// To guarantee that *all* screens use the same (effective) rate, we compute it
// centrally when mapping the profile row.
const BASE_URSSAF_BY_ACTIVITY_TYPE = {
  services: getDefaultUrssafRatePercent({ activity_type: 'services' }),
  sales: 12.3,
  mixed: getDefaultUrssafRatePercent({ activity_type: 'mixed' }),
};

function monthsBetween(startDate, nowDate) {
  const s = new Date(startDate);
  const n = new Date(nowDate);
  if (Number.isNaN(s.getTime()) || Number.isNaN(n.getTime())) return null;
  let months = (n.getFullYear() - s.getFullYear()) * 12 + (n.getMonth() - s.getMonth());
  // If we haven't reached the day-of-month yet, subtract 1 month.
  if (n.getDate() < s.getDate()) months -= 1;
  return Math.max(0, months);
}

function computeEffectiveUrssafRatePercent({ baseRatePercent, acreEnabled, acreStartDate, acreDurationMonths, now = new Date() }) {
  const base = Number(baseRatePercent);
  if (!Number.isFinite(base) || base <= 0) return base;
  if (!acreEnabled || !acreStartDate) return base;

  const months = monthsBetween(acreStartDate, now);
  if (months == null) return base;

  const duration = Number.isFinite(Number(acreDurationMonths)) ? Number(acreDurationMonths) : 36;
  if (months >= duration) return base;

  let factor = 1;
  if (months < 12) factor = 0.5;       // 50% reduction first year
  else if (months < 24) factor = 0.75; // 25% reduction second year
  else if (months < 36) factor = 0.90; // 10% reduction third year

  return Math.round((base * factor) * 100) / 100;
}

function mapProfileRow(row) {
  const rates = row?.rates_json || {};
  const taxTerritory = normalizeTerritory(rates?.tax_territory);
  const domUrssafReductionEnabled = rates?.dom_urssaf_reduction_enabled === true;
  const domUrssafPeriod = (typeof rates?.dom_urssaf_period === 'number' && Number.isFinite(rates.dom_urssaf_period))
    ? rates.dom_urssaf_period
    : 1;

  const acreEnabled = row?.acre === true || rates?.acre_enabled === true;
  const acreStartDate = rates?.acre_start_date || null;
  const acreDurationMonths = typeof rates?.acre_duration_months === 'number' ? rates.acre_duration_months : 36;
  const activityType = typeof rates?.activity_type === 'string' ? rates.activity_type : null;

  const hasManualBaseRate = (typeof rates.urssaf_base_rate_percent === 'number' && Number.isFinite(rates.urssaf_base_rate_percent));
  let baseUrssaf = hasManualBaseRate
    ? rates.urssaf_base_rate_percent
    : (activityType && BASE_URSSAF_BY_ACTIVITY_TYPE[activityType])
      ? BASE_URSSAF_BY_ACTIVITY_TYPE[activityType]
      : (typeof rates.urssaf_rate_percent === 'number' ? rates.urssaf_rate_percent : getDefaultUrssafRatePercent(rates));

  // Optional DOM reduced rates preset (progressive). Only applied when the user enabled it
  // and there is no manual base override.
  if (!hasManualBaseRate && domUrssafReductionEnabled) {
    const domBase = getDomUrssafBaseRatePercent({ activityType: activityType || 'services', period: domUrssafPeriod });
    if (typeof domBase === 'number' && Number.isFinite(domBase) && domBase > 0) {
      baseUrssaf = domBase;
    }
  }

  const storedUrssaf = (typeof rates.urssaf_rate_percent === 'number' && Number.isFinite(rates.urssaf_rate_percent))
    ? rates.urssaf_rate_percent
    : baseUrssaf;
  const effectiveUrssaf = computeEffectiveUrssafRatePercent({
    baseRatePercent: baseUrssaf,
    acreEnabled,
    acreStartDate,
    acreDurationMonths,
  });
  const urssafRateForApp = (acreEnabled && acreStartDate) ? effectiveUrssaf : storedUrssaf;
  return {
    id: row.user_id,
    user_id: row.user_id,
    onboarding_completed: !!row.onboarding_completed,
    activity_label: row.activity_label || '',
    first_name: rates.first_name || '',
    tva_frequency: row.tva_frequency || 'trimestrielle',
    // TVA details stored in rates_json
    tva_regime: isVatApplicableInTerritory(taxTerritory)
      ? (rates.tva_regime || (row.tva_mode === 'reel' ? 'reel_normal' : 'franchise'))
      : 'franchise',
    tva_due_day: typeof rates.tva_due_day === 'number' ? rates.tva_due_day : 19,
    urssaf_override_due_date: rates.urssaf_override_due_date || null,
    // UI expects these names (legacy from Base44 generation)
    declaration_frequency: row.urssaf_frequency === 'monthly' ? 'monthly' : 'quarterly',
    versement_liberatoire: row.tax_mode === 'versement_liberatoire',
    vat_mode: (isVatApplicableInTerritory(taxTerritory) && row.tva_mode === 'reel') ? 'reel' : 'not_applicable',
    vat_rate_percent: typeof rates.vat_rate_percent === 'number' ? rates.vat_rate_percent : getDefaultVatRatePercent(taxTerritory),
    // IMPORTANT: ACRE reduction must affect *all* calculations of taxes/charges.
    // We compute the effective rate here so every screen stays consistent.
    urssaf_rate_percent: typeof urssafRateForApp === 'number' ? urssafRateForApp : (typeof rates.urssaf_rate_percent === 'number' ? rates.urssaf_rate_percent : getDefaultUrssafRatePercent(rates)),
    tax_rate_percent: typeof rates.tax_rate_percent === 'number' ? rates.tax_rate_percent : getDefaultVersementLiberatoireRatePercent(rates),
    // Fiscal territory
    tax_territory: taxTerritory,
    dom_urssaf_reduction_enabled: domUrssafReductionEnabled,
    dom_urssaf_period: domUrssafPeriod,
    cfe_annual_amount: typeof row.cfe_estimate_annual === 'number' ? row.cfe_estimate_annual : 0,

    // ACRE details (optional UI usage)
    acre_enabled: acreEnabled,
    acre_start_date: acreStartDate,
    acre_duration_months: acreDurationMonths,

    // Rémunération / Trésorerie (stockées dans rates_json pour rester compatible)
    cash_balance: typeof rates.cash_balance === 'number' ? rates.cash_balance : 0,
    cash_balance_set: rates.cash_balance_set === true || rates.cash_balance !== undefined,
    cash_balance_updated_at: rates.cash_balance_updated_at || null,
    salary_cash_balance: typeof rates.salary_cash_balance === 'number' ? rates.salary_cash_balance : null,
    salary_cash_balance_set: rates.salary_cash_balance_set === true || rates.salary_cash_balance !== undefined,
    salary_cash_balance_updated_at: rates.salary_cash_balance_updated_at || null,
    payout_horizon_days: typeof rates.payout_horizon_days === 'number' ? rates.payout_horizon_days : 30,
    payout_mode: typeof rates.payout_mode === 'string' ? rates.payout_mode : 'normal',
    payout_safety_buffer: typeof rates.payout_safety_buffer === 'number' ? rates.payout_safety_buffer : 200,
    payout_calc_type: typeof rates.payout_calc_type === 'string' ? rates.payout_calc_type : 'one_shot',
    // Timezone du compte (pour le calcul des mois / historiques). Stockée dans rates_json.
    timezone: (typeof rates.timezone === 'string' && rates.timezone) ? rates.timezone : 'Europe/Paris',
    locale: rates.locale || 'fr',
    notification_preferences: rates.notification_preferences || {
      in_app_enabled: true,
      email_enabled: true,
      push_enabled: false,
      days_before: [7, 3, 1],
    },
    rates_json: rates,
  };
}

function createProfileEntity() {
  const api = {
    async filter(filterObj = {}) {
      const user = await base44.auth.me();
      if (filterObj?.user_id && filterObj.user_id !== user.id) return [];

      // Ensure row exists (for legacy users)
      await supabase.from('profiles').upsert({ user_id: user.id }, { onConflict: 'user_id' });

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data ? [mapProfileRow(data)] : [];
    },

    async get(id) {
      const rows = await api.filter({ user_id: id });
      return rows[0] || null;
    },

    async create(data) {
      // Create behaves like upsert
      return api.update(data?.user_id, data);
    },

    async update(id, patch) {
      const user = await base44.auth.me();
      if (id && id !== user.id) throw new Error('Forbidden');

      const { data: existing, error: readErr } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (readErr) throw readErr;

      const prevRates = existing?.rates_json || {};
      const nextRates = {
        ...prevRates,
      };

      // Rates + misc stored in rates_json
      // NOTE: onboarding stores some context here so the app can recompute rates consistently.
      if (patch?.activity_type !== undefined) nextRates.activity_type = patch.activity_type;
      if (patch?.urssaf_base_rate_percent !== undefined) nextRates.urssaf_base_rate_percent = patch.urssaf_base_rate_percent;
      if (patch?.tax_territory !== undefined) nextRates.tax_territory = normalizeTerritory(patch.tax_territory);
      if (patch?.dom_urssaf_reduction_enabled !== undefined) nextRates.dom_urssaf_reduction_enabled = !!patch.dom_urssaf_reduction_enabled;
      if (patch?.dom_urssaf_period !== undefined) nextRates.dom_urssaf_period = Number(patch.dom_urssaf_period) || 1;
      if (patch?.acre_start_date !== undefined) nextRates.acre_start_date = patch.acre_start_date;
      if (patch?.acre_duration_months !== undefined) nextRates.acre_duration_months = patch.acre_duration_months;
      if (patch?.tax_rate_percent !== undefined) nextRates.tax_rate_percent = patch.tax_rate_percent;
      if (patch?.urssaf_rate_percent !== undefined) nextRates.urssaf_rate_percent = patch.urssaf_rate_percent;
      if (patch?.vat_rate_percent !== undefined) nextRates.vat_rate_percent = patch.vat_rate_percent;
      if (patch?.first_name !== undefined) nextRates.first_name = patch.first_name;
      if (patch?.locale !== undefined) nextRates.locale = patch.locale;
      if (patch?.timezone !== undefined) nextRates.timezone = String(patch.timezone);
      if (patch?.notification_preferences !== undefined) nextRates.notification_preferences = patch.notification_preferences;
      if (patch?.urssaf_override_due_date !== undefined) nextRates.urssaf_override_due_date = patch.urssaf_override_due_date;
      if (patch?.tva_regime !== undefined) nextRates.tva_regime = patch.tva_regime;
      if (patch?.tva_due_day !== undefined) nextRates.tva_due_day = Number(patch.tva_due_day);

      // Rémunération / Trésorerie (stockées dans rates_json)
      if (patch?.cash_balance !== undefined) { nextRates.cash_balance = Number(patch.cash_balance) || 0; nextRates.cash_balance_set = true; nextRates.cash_balance_updated_at = nowIso(); }
      if (patch?.salary_cash_balance !== undefined) { nextRates.salary_cash_balance = Number(patch.salary_cash_balance) || 0; nextRates.salary_cash_balance_set = true; nextRates.salary_cash_balance_updated_at = nowIso(); }
      if (patch?.payout_horizon_days !== undefined) nextRates.payout_horizon_days = Number(patch.payout_horizon_days) || 30;
      if (patch?.payout_mode !== undefined) nextRates.payout_mode = String(patch.payout_mode);
      if (patch?.payout_safety_buffer !== undefined) nextRates.payout_safety_buffer = Number(patch.payout_safety_buffer) || 0;
      if (patch?.payout_calc_type !== undefined) nextRates.payout_calc_type = String(patch.payout_calc_type);

      // Facturation (template / mentions / logo) stockées dans rates_json
      if (patch?.invoice_logo_dataurl !== undefined) nextRates.invoice_logo_dataurl = patch.invoice_logo_dataurl;
      if (patch?.invoice_header_color !== undefined) nextRates.invoice_header_color = String(patch.invoice_header_color);
      if (patch?.invoice_company_name !== undefined) nextRates.invoice_company_name = patch.invoice_company_name;
      if (patch?.invoice_company_address !== undefined) nextRates.invoice_company_address = patch.invoice_company_address;
      if (patch?.invoice_company_postal_code !== undefined) nextRates.invoice_company_postal_code = patch.invoice_company_postal_code;
      if (patch?.invoice_company_city !== undefined) nextRates.invoice_company_city = patch.invoice_company_city;
      if (patch?.invoice_company_country !== undefined) nextRates.invoice_company_country = patch.invoice_company_country;
      if (patch?.invoice_company_vat_number !== undefined) nextRates.invoice_company_vat_number = patch.invoice_company_vat_number;
      if (patch?.invoice_company_iban !== undefined) nextRates.invoice_company_iban = patch.invoice_company_iban;
      if (patch?.invoice_payment_terms_label !== undefined) nextRates.invoice_payment_terms_label = patch.invoice_payment_terms_label;
      if (patch?.invoice_payment_terms_days !== undefined) nextRates.invoice_payment_terms_days = Number(patch.invoice_payment_terms_days) || 0;
      if (patch?.invoice_company_email !== undefined) nextRates.invoice_company_email = patch.invoice_company_email;
      if (patch?.invoice_company_phone !== undefined) nextRates.invoice_company_phone = patch.invoice_company_phone;
      if (patch?.invoice_company_siret !== undefined) nextRates.invoice_company_siret = patch.invoice_company_siret;
      if (patch?.invoice_payment_details !== undefined) nextRates.invoice_payment_details = patch.invoice_payment_details;
      if (patch?.invoice_footer_note !== undefined) nextRates.invoice_footer_note = patch.invoice_footer_note;
      if (patch?.invoice_prefix !== undefined) nextRates.invoice_prefix = patch.invoice_prefix;
      if (patch?.invoice_number_padding !== undefined) nextRates.invoice_number_padding = Number(patch.invoice_number_padding);
      if (patch?.invoice_next_seq !== undefined) nextRates.invoice_next_seq = Number(patch.invoice_next_seq);

      // Territory invariants
      // - Guyane : TVA non applicable => force franchise + taux TVA 0
      // - Other territories: keep user choice
      const territory = normalizeTerritory(nextRates.tax_territory);
      if (!isVatApplicableInTerritory(territory)) {
        nextRates.vat_rate_percent = 0;
        nextRates.tva_regime = 'franchise';
      } else {
        // If no explicit VAT rate was ever set, keep a sensible default.
        if (nextRates.vat_rate_percent === undefined || nextRates.vat_rate_percent === null) {
          nextRates.vat_rate_percent = getDefaultVatRatePercent(territory);
        }
      }

      const upsertPayload = {
        user_id: user.id,
        onboarding_completed: patch?.onboarding_completed ?? existing?.onboarding_completed ?? false,
        activity_label: patch?.activity_label ?? existing?.activity_label ?? null,
        urssaf_frequency:
          patch?.declaration_frequency === 'monthly'
            ? 'monthly'
            : patch?.declaration_frequency === 'quarterly'
              ? 'quarterly'
              : (existing?.urssaf_frequency || 'quarterly'),
        tax_mode:
          patch?.versement_liberatoire === true
            ? 'versement_liberatoire'
            : patch?.versement_liberatoire === false
              ? 'classique'
              : (existing?.tax_mode || 'classique'),
        acre: patch?.acre_enabled !== undefined ? !!patch.acre_enabled : (existing?.acre || false),
        tva_mode: (() => {
          const territory = normalizeTerritory(nextRates.tax_territory);
          if (!isVatApplicableInTerritory(territory)) return 'franchise';
          return patch?.vat_mode === 'reel'
            ? 'reel'
            : patch?.vat_mode === 'not_applicable'
              ? 'franchise'
              : (existing?.tva_mode || 'franchise');
        })(),
        tva_frequency: (patch?.tva_frequency ?? existing?.tva_frequency ?? 'trimestrielle'),
        cfe_enabled: (patch?.cfe_annual_amount ?? (existing?.cfe_estimate_annual || 0)) > 0,
        cfe_estimate_annual:
          (patch?.cfe_annual_amount ?? (existing?.cfe_estimate_annual || 0)) > 0
            ? (patch?.cfe_annual_amount ?? existing?.cfe_estimate_annual)
            : null,
        rates_json: nextRates,
      };

      const { error: upErr } = await supabase
        .from('profiles')
        .upsert(upsertPayload, { onConflict: 'user_id' });

      if (upErr) throw upErr;

      const { data: row, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      return mapProfileRow(row);
    },

    async delete() {
      throw new Error('Not supported');
    },
  };
  return api;
}

function createAppUsersEntity() {
  return {
    async filter(filterObj = {}) {
      const user = await base44.auth.me();
      if (filterObj?.id && filterObj.id !== user.id) return [];
      if (filterObj?.user_id && filterObj.user_id !== user.id) return [];

      // NOTE: We intentionally avoid client-side UPSERT on app_users.
      // With RLS enabled, an UPSERT implies INSERT and often fails (403) unless an INSERT policy exists.
      // app_users rows are normally created by the DB trigger on signup.
      // For legacy accounts without a row, we fallback to a local "free" plan object.

      const { data, error } = await supabase
        .from('app_users')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      const row = data || { user_id: user.id, email: user.email, plan: 'free', plan_status: 'active' };

      return [{
        id: row.user_id,
        user_id: row.user_id,
        email: row.email,
        // IMPORTANT: do NOT override the subscription plan with "pack".
        // Pack is tracked separately via pack_active_until so Pro users keep Pro.
        plan: row.plan || 'free',
        plan_status: row.plan_status || 'active',
        plan_access_override: row.plan_access_override,
        plan_access_override_until: row.plan_access_override_until,
        is_ambassador: row.is_ambassador,
        ambassador_since: row.ambassador_since,
        // Stripe / billing fields (used by Billing.jsx)
        stripe_customer_id: row.stripe_customer_id,
        stripe_subscription_id: row.stripe_subscription_id,
        stripe_price_id: row.stripe_price_id,
        current_period_start: row.current_period_start,
        current_period_end: row.current_period_end,
        cancel_at_period_end: row.cancel_at_period_end,
        trial_start: row.trial_start,
        trial_end: row.trial_end,
        pro_trial_used: row.pro_trial_used,
        pack_active_until: row.pack_active_until,
        pack_started_at: row.pack_started_at,
        // DB column name: pack_purchased_one
        pack_purchased_one: row.pack_purchased_one,
        // Starter invoicing guardrail (lifetime counter)
        starter_factures_created: row.starter_factures_created,
        ever_subscribed: row.ever_subscribed,
        is_admin: row.is_admin,
      }];
    },
  };
}

function createNotificationSettingsEntity() {
  const api = {
    async filter(filterObj = {}) {
      const user = await base44.auth.me();
      if (filterObj?.user_id && filterObj.user_id !== user.id) return [];

      const { data, error } = await supabase
        .from('notification_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (!data) return [];

      const payload = data.payload || {};
      return [{
        id: data.user_id,
        user_id: data.user_id,
        // Spread payload first (legacy keys may include email_enabled); columns below must win.
        ...payload,
        email_enabled: !!data.email_enabled,
        email_enabled_touched: !!data.email_enabled_touched,
        email: data.email,
        reminders_enabled: !!data.reminders_enabled,
        timezone: data.timezone,
      }];
    },

    async create(data) {
      const user = await base44.auth.me();
      const payload = { ...(data || {}) };
      delete payload.id;
      delete payload.user_id;
      delete payload.email_enabled;
      delete payload.email_enabled_touched;
      delete payload.email;
      delete payload.reminders_enabled;
      delete payload.timezone;

      const { error } = await supabase
        .from('notification_settings')
        .upsert({
          user_id: user.id,
          email_enabled: data.email_enabled ?? false,
          email_enabled_touched: data.email_enabled_touched ?? false,
          email: data.email ?? user.email ?? null,
          reminders_enabled: data.reminders_enabled ?? true,
          timezone: data.timezone ?? 'Europe/Paris',
          payload,
        }, { onConflict: 'user_id' });

      if (error) throw error;
      const rows = await api.filter({ user_id: user.id });
      return rows[0] || null;
    },

    async update(id, patch) {
      const user = await base44.auth.me();
      if (id && id !== user.id) throw new Error('Forbidden');

      const { data: existing, error: readErr } = await supabase
        .from('notification_settings')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (readErr) throw readErr;

      const prevPayload = existing?.payload || {};
      const nextPayload = { ...prevPayload };

      const clean = { ...(patch || {}) };
      delete clean.id;
      delete clean.user_id;

      // Column-like fields
      const email_enabled = clean.email_enabled !== undefined ? !!clean.email_enabled : (existing?.email_enabled ?? false);
      const email_enabled_touched = clean.email_enabled_touched !== undefined
        ? !!clean.email_enabled_touched
        : (existing?.email_enabled_touched ?? false);
      const email = clean.email !== undefined ? clean.email : (existing?.email ?? user.email ?? null);
      const reminders_enabled = clean.reminders_enabled !== undefined ? !!clean.reminders_enabled : (existing?.reminders_enabled ?? true);
      const timezone = clean.timezone !== undefined ? clean.timezone : (existing?.timezone ?? 'Europe/Paris');

      delete clean.email_enabled;
      delete clean.email_enabled_touched;
      delete clean.email;
      delete clean.reminders_enabled;
      delete clean.timezone;

      Object.assign(nextPayload, clean);

      const { error } = await supabase
        .from('notification_settings')
        .upsert({
          user_id: user.id,
          email_enabled,
          email_enabled_touched,
          email,
          reminders_enabled,
          timezone,
          payload: nextPayload,
        }, { onConflict: 'user_id' });

      if (error) throw error;
      const rows = await api.filter({ user_id: user.id });
      return rows[0] || null;
    },

    async delete() {
      throw new Error('Not supported');
    },
  };
  return api;
}

function createReminderSettingsEntity() {
  // Backed by public.reminder_settings (id UUID PK) with a JSONB payload.
  const DEFAULTS = {
    email_enabled: false,
    reminder_days: [7, 5, 3, 1],
    timezone: 'Europe/Paris',
  };

  const safeParseJson = (v, fallback) => {
    try {
      return JSON.parse(v);
    } catch {
      return fallback;
    }
  };

  const normalizeDays = (payload) => {
    if (Array.isArray(payload?.reminder_days) && payload.reminder_days.length) return payload.reminder_days;
    if (typeof payload?.reminder_days_json === 'string') {
      const parsed = safeParseJson(payload.reminder_days_json, DEFAULTS.reminder_days);
      if (Array.isArray(parsed) && parsed.length) return parsed;
    }
    return DEFAULTS.reminder_days;
  };

  const rowToEntity = (row) => {
    const rawPayload = (row && typeof row.payload === 'object') ? row.payload : null;
    const fallbackPayload = {
      email_enabled: row?.email_enabled,
      reminder_days_json: row?.reminder_days_json,
      reminder_days: row?.reminder_days,
      timezone: row?.timezone,
      enabled: row?.enabled,
    };
    const payload = (rawPayload && Object.keys(rawPayload).length) ? rawPayload : fallbackPayload;

    const days = normalizeDays(payload);

    return {
      id: row.id,
      user_id: row.user_id,

      // Fields used by the Settings UI (kept stable even if DB schema changes)
      email_enabled: payload.email_enabled ?? DEFAULTS.email_enabled,
      reminder_days_json: JSON.stringify(days),
      timezone: payload.timezone || DEFAULTS.timezone,

      // Compatibility aliases (older UI/logic may look for these)
      enabled: payload.enabled ?? payload.email_enabled ?? DEFAULTS.email_enabled,

      created_date: row.created_at,
      updated_date: row.updated_at,
    };
  };

  const buildPayload = (input = {}, existingPayload = {}) => {
    const out = { ...(existingPayload || {}) };

    if (typeof input.email_enabled === 'boolean') out.email_enabled = input.email_enabled;

    // Accept either reminder_days_json (stringified array) or reminder_days (array)
    if (typeof input.reminder_days_json === 'string') {
      const parsed = safeParseJson(input.reminder_days_json, normalizeDays(existingPayload));
      out.reminder_days = Array.isArray(parsed) ? parsed : normalizeDays(existingPayload);
      out.reminder_days_json = JSON.stringify(out.reminder_days);
    } else if (Array.isArray(input.reminder_days)) {
      out.reminder_days = input.reminder_days;
      out.reminder_days_json = JSON.stringify(out.reminder_days);
    }

    if (typeof input.timezone === 'string' && input.timezone.trim()) out.timezone = input.timezone.trim();

    // Keep "enabled" in sync if provided (some callers might send enabled)
    if (typeof input.enabled === 'boolean' && typeof input.email_enabled !== 'boolean') {
      out.email_enabled = input.enabled;
    }

    // Always ensure we have defaults (important for new rows)
    if (typeof out.email_enabled !== 'boolean') out.email_enabled = DEFAULTS.email_enabled;
    if (!Array.isArray(out.reminder_days)) out.reminder_days = normalizeDays(out);
    if (typeof out.timezone !== 'string' || !out.timezone) out.timezone = DEFAULTS.timezone;

    // Keep a stable mirror for older code
    out.enabled = out.email_enabled;

    return out;
  };

  const api = {
    async filter(filterObj = {}, sort, limit) {
      const user = await base44.auth.me();
      if (filterObj?.user_id && filterObj.user_id !== user.id) return [];

      const { data, error } = await supabase
        .from('reminder_settings')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;

      let out = (data || []).map(rowToEntity);

      out = applyFilter(out, filterObj);
      out = applySort(out, sort);
      if (typeof limit === 'number') out = out.slice(0, limit);
      return out;
    },

    async list(sort = '-created_date', limit = 50) {
      return api.filter({}, sort, limit);
    },

    async create(input = {}) {
      const user = await base44.auth.me();

      const payload = buildPayload(input, {});

      // Try schema with a JSONB 'payload' column (preferred)
      let row = null;
      let error = null;

      ({ data: row, error } = await supabase
        .from('reminder_settings')
        .insert({
          user_id: user.id,
          payload,
        })
        .select('*')
        .single());

      if (error) {
        const msg = String(error?.message || error?.details || '');
        // Fallback for legacy schema where columns are stored directly (no 'payload' column)
        if (msg.toLowerCase().includes('payload')) {
          const direct = {
            user_id: user.id,
            email_enabled: !!payload.email_enabled,
            reminder_days_json: payload.reminder_days_json || JSON.stringify(payload.reminder_days || [7, 5, 3, 1]),
            timezone: payload.timezone || 'Europe/Paris',
          };

          let row2 = null;
          let error2 = null;

          ({ data: row2, error: error2 } = await supabase
            .from('reminder_settings')
            .insert(direct)
            .select('*')
            .single());

          if (error2) throw error2;
          return rowToEntity(row2);
        }
        throw error;
      }

      return rowToEntity(row);
    },

    async update(id, patch = {}) {
      const user = await base44.auth.me();

      const { data: existing, error: readErr } = await supabase
        .from('reminder_settings')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .single();

      if (readErr) throw readErr;

      const existingPayload =
        existing && typeof existing.payload === 'object' && existing.payload && Object.keys(existing.payload).length
          ? existing.payload
          : {
              email_enabled: existing?.email_enabled,
              reminder_days_json: existing?.reminder_days_json,
              reminder_days: existing?.reminder_days,
              timezone: existing?.timezone,
              enabled: existing?.enabled,
            };

      const payload = buildPayload(patch, existingPayload);

      // Try schema with JSONB 'payload'
      let row = null;
      let error = null;

      ({ data: row, error } = await supabase
        .from('reminder_settings')
        .update({
          payload,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .eq('user_id', user.id)
        .select('*')
        .single());

      if (error) {
        const msg = String(error?.message || error?.details || '');
        // Fallback for legacy schema without 'payload' column
        if (msg.toLowerCase().includes('payload')) {
          const directUpdate = {
            email_enabled: !!payload.email_enabled,
            reminder_days_json: payload.reminder_days_json || JSON.stringify(payload.reminder_days || [7, 5, 3, 1]),
            timezone: payload.timezone || 'Europe/Paris',
          };

          let row2 = null;
          let error2 = null;

          // Try with updated_at when available
          ({ data: row2, error: error2 } = await supabase
            .from('reminder_settings')
            .update({ ...directUpdate, updated_at: new Date().toISOString() })
            .eq('id', id)
            .eq('user_id', user.id)
            .select('*')
            .single());

          if (error2) {
            const msg2 = String(error2?.message || error2?.details || '');
            if (msg2.toLowerCase().includes('updated_at')) {
              ({ data: row2, error: error2 } = await supabase
                .from('reminder_settings')
                .update(directUpdate)
                .eq('id', id)
                .eq('user_id', user.id)
                .select('*')
                .single());
            }
          }

          if (error2) throw error2;
          return rowToEntity(row2);
        }
        throw error;
      }

      return rowToEntity(row);
    },

    async delete(id) {
      const user = await base44.auth.me();
      const { error } = await supabase
        .from('reminder_settings')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);
      if (error) throw error;
      return true;
    },
  };

  return api;
}

function createReminderLogEntity() {
  const api = {
    async list(sort = '-sent_at', limit = 50) {
      const user = await base44.auth.me();
      const { data, error } = await supabase
        .from('reminder_log')
        .select('*')
        .eq('user_id', user.id)
        .order('sent_at', { ascending: false })
        .limit(limit);

      if (error) throw error;

      return (data || []).map((r) => ({
        id: r.id,
        user_id: r.user_id,
        deadline_id: r.deadline_id,
        sent_at: r.sent_at,
        channel: r.channel,
        status: r.status,
        payload: r.payload || {},
      }));
    },

    async create(input = {}) {
      const user = await base44.auth.me();

      // Ensure dedupe_key lives inside payload for the unique index on (payload->>'dedupe_key')
      const payload = {
        ...(input?.payload && typeof input.payload === 'object' ? input.payload : {}),
        ...input,
      };

      // Avoid accidental duplication of top-level keys inside payload
      delete payload.id;
      delete payload.user_id;
      delete payload.created_at;
      delete payload.sent_at;
      delete payload.channel;
      delete payload.status;
      delete payload.deadline_id;

      const row = {
        user_id: user.id,
        deadline_id: input?.deadline_id ?? null,
        sent_at: input?.sent_at ?? nowIso(),
        channel: input?.channel ?? 'in_app',
        status: input?.status ?? 'sent',
        payload,
      };

      const { data, error } = await supabase
        .from('reminder_log')
        .insert(row)
        .select('*')
        .single();

      if (error) {
        // Unique violation => already logged (dedupe)
        if (error.code === '23505') return { duplicate: true };
        throw error;
      }

      return {
        id: data.id,
        user_id: data.user_id,
        deadline_id: data.deadline_id,
        sent_at: data.sent_at,
        channel: data.channel,
        status: data.status,
        payload: data.payload || {},
        created_at: data.created_at,
      };
    },


    async filter(filterObj = {}, sort, limit) {
      // fallback to list
      return api.list(sort, limit);
    },
  };
  return api;
}

function createAiReportsEntity() {
  const api = {
    async list(filterObj = {}, sort = '-created_at', limit = 20) {
      const user = await base44.auth.me();

      const qb = supabase
        .from('ai_reports')
        .select('*')
        .eq('user_id', user.id);

      if (filterObj?.period_month) qb.eq('period_month', filterObj.period_month);
      if (filterObj?.created_from) qb.gte('created_at', filterObj.created_from);
      if (filterObj?.created_to) qb.lt('created_at', filterObj.created_to);

      const asc = typeof sort === 'string' && sort.startsWith('-') ? false : true;
      const orderCol = typeof sort === 'string' ? sort.replace('-', '') : 'created_at';

      const { data, error } = await qb
        .order(orderCol || 'created_at', { ascending: asc })
        .limit(typeof limit === 'number' ? limit : 20);

      if (error) throw error;
      return (data || []).map((r) => ({
        id: r.id,
        user_id: r.user_id,
        period_month: r.period_month,
        model: r.model,
        prompt_version: r.prompt_version,
        content_markdown: r.content_markdown,
        created_at: r.created_at,
      }));
    },

    async count(filterObj = {}) {
      const user = await base44.auth.me();
      const qb = supabase
        .from('ai_reports')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id);
      if (filterObj?.created_from) qb.gte('created_at', filterObj.created_from);
      if (filterObj?.created_to) qb.lt('created_at', filterObj.created_to);
      if (filterObj?.period_month) qb.eq('period_month', filterObj.period_month);
      const { count, error } = await qb;
      if (error) throw error;
      return count || 0;
    },
  };
  return api;
}



export const base44 = {
  auth: {
    async me() {
      const { data, error } = await supabase.auth.getUser();
      if (error || !data?.user) throw error || new Error('Not authenticated');
      return {
        id: data.user.id,
        email: data.user.email,
        // For convenience in UI
        email_confirmed_at: data.user.email_confirmed_at,
      };
    },

    async logout() {
      await supabase.auth.signOut();
      // SPA navigation (no full page reload)
      // After logout, always return to public home page.
      // (Some hosts don't serve client routes like /landing without rewrite rules.)
      window.history.pushState({}, '', '/');
      window.dispatchEvent(new PopStateEvent('popstate'));
    },

    redirectToLogin(nextUrl = '/dashboard') {
      const next = encodeURIComponent(nextUrl);
      const url = `/login?next=${next}`;
      window.history.pushState({}, '', url);
      window.dispatchEvent(new PopStateEvent('popstate'));
    },
  },

  // Entities used throughout the app.
  // Keeping names identical avoids rewriting the rest of the code right now.
  entities: {
    User: createAppUsersEntity(),

    Profile: createProfileEntity(),
    // Activites (optionnel) : fallback localStorage si la table n'existe pas encore
    Activity: createSupabaseEntitySafe('activities', 'activities'),

    Revenue: createSupabaseEntity('revenues'),
    Charge: createSupabaseEntity('charges'),
    Facture: createSupabaseEntity('factures'),
    ReceiptBook: createSupabaseEntity('receipts_book'),
    Devis: createSupabaseEntity('devis'),

    // Facturation (clients) - table optionnelle, fallback localStorage si absente
    Customer: createSupabaseEntitySafe('customers', 'customers'),

    Envelope: createSupabaseEntity('envelopes'),
    Deadline: createSupabaseEntity('deadlines'),
    VatPeriod: createSupabaseEntity('vat_periods'),

    Goal: createSupabaseEntity('goals'),
    GoalContribution: createSupabaseEntity('goal_contributions'),

    Export: createSupabaseEntity('exports'),

    Notification: createSupabaseEntity('notifications'),
    NotificationRead: createSupabaseEntity('notification_reads'),
    CookieConsent: createSupabaseEntity('cookie_consents'),
    UnsubscribeToken: createSupabaseEntity('unsubscribe_tokens'),
    PayoutHistory: createSupabaseEntity('payout_history'),

    // Rapports IA (table non-payload)
    AIReport: createAiReportsEntity(),

    // Trésorerie : historique mensuel (table optionnelle, fallback localStorage si absente)
    CashLog: createSupabaseEntitySafe('cash_logs'),

    // Billing / reminders / admin
    NotificationSettings: createNotificationSettingsEntity(),
    ReminderSettings: createReminderSettingsEntity(),
    ReminderLog: createReminderLogEntity(),

    // Extra entities used by some pages/components (require extra tables - see supabase/extra_schema.sql)
    Provision: createSupabaseEntitySafe('provisions'),
    QuarterSummary: createSupabaseEntitySafe('quarter_summaries'),
    Reminder: createSupabaseEntitySafe('reminders'),
    Payment: createSupabaseEntitySafe('payments'),
    PaymentLog: createSupabaseEntitySafe('payment_logs'),
    PrevisionTresorerie: createSupabaseEntitySafe('prevision_tresorerie'),
    TaxEntity: createSupabaseEntitySafe('tax_entities'),
    TaxActivity: createSupabaseEntitySafe('tax_activities'),
  },

  functions: functionsProxy,

  integrations: {
    Core: {
      async InvokeLLM() {
        return {
          success: false,
          output:
            "Le moteur IA n'est pas configuré (mode démo). Branchez-le via une Edge Function Supabase ou un backend." ,
        };
      },
    },
  },
};